import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { BusyIcon } from '../../../components/loading';
import { DateFilterSelect, monthsOf, type DateFilterId, DEFAULT_DATE_FILTER } from './IcmDateFilter';
import { ICM_LIST_TAKE, icmTruncationNote } from '../icmPaging';
import { useAuthStore } from '../../../auth/store';
import { useSaveFlow, useConfirm } from '../../../components/confirm';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';

import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import './IcmDetailGrid.css';

/** Receiving Items MASTER row — one PURCHASE ORDER with lines still pending
 *  receipt. Legacy Requisition_ListView_Receive_MainReceive groups the pending
 *  requisitions by POID and shows one row per PO (ReceiveViewController.cs
 *  ~283-300), which is why this screen is a master/detail rather than a flat
 *  line list. */
interface ReceivePoRow {
  poId: string;
  poNumber: string | null;
  vendorName: string | null;
  numItems: number;
  expectDate: string | null;
  trackingNumber: string | null;
}

/** Receiving Items HISTORY row — ONE RECEIPT.
 *
 *  Legacy's "History" action here does NOT re-filter the purchase-order master:
 *  Receiveview_Execute (ReceiveViewController ~537) REPLACES the view with
 *  `Distribution_ListView_ReceiveView`, a flat list of the receipts themselves
 *  with Criteria="" — every receipt, not only those still to distribute. That
 *  list is where legacy puts "Rollback" and the Date Filter, which is why both
 *  were unreachable while History only widened the PO list. */
interface ReceiveHistoryRow {
  oid: string;
  receiveId: string | null;
  divisionName: string | null;
  itemName: string | null;
  specification: string | null;
  catalog: string | null;
  packUnits: string | null;
  itemUnit: number | null;
  gradeName: string | null;
  departmentName: string | null;
  stockQty: number | null;
  itemReceiving: number | null;
  receiveDate: string | null;
  receivedByName: string | null;
  statusLabel: string;
  status: number;
}

interface PendingReceiveRow {
  oid: string;
  poId: string | null;
  rqId: string | null;
  itemName: string;
  vendorName: string | null;
  orderQty: number;
  totalItems: number;
  itemRemaining: number | null;
  // The rest of the legacy Requisition_ListView_Receive column set.
  itemReceived: string | null;        // the STORED "1 of 2"
  specification: string | null;       // legacy caption "Description"
  catalog: string | null;
  departmentName: string | null;
  packUnits: string | null;
  itemUnit: number | null;
  stockQty: number | null;
  statusLabel: string | null;
  receiveDate: string | null;
  receivedByName: string | null;
}

/** The IN-GRID working state of one line, exactly the four cells legacy's batch
 *  editor drives (RequisitionViewcontroller.cs ~560-760): the scratch
 *  #ItemReceiving (a [NonPersistent] BO property, so always 0 on load), plus the
 *  three display cells it recomputes. `base*` is the row as LOADED — legacy keeps
 *  the same snapshot in sessionStorage so unticking can restore it. */
interface ReceiveEdit {
  receiving: number;
  remaining: number;
  received: string;
  receiveDate: string | null;
  receivedBy: string | null;
  baseRemaining: number;
  baseReceivedCount: number;
  baseReceivedFull: string;
  /** 'row' = set by ticking the checkbox, 'column' = typed into #ItemReceiving.
   *  Legacy will not let a tick overwrite a typed quantity. */
  source: '' | 'row' | 'column';
}

export default function ReceiveItemsPage() {
  const notify = useNotify();
  const saveFlow = useSaveFlow();
  const { confirm, prompt } = useConfirm();
  // MAC rights (hide, never grey): receiving (Save, the #ItemReceiving cell) and Rollback = Write.
  const perm = useNavActions('icm-receive-items');
  // ReceivedBy is stamped SERVER-side on save (@ActorOid); this is only the
  // name the grid previews in the meantime, which is what legacy's
  // SetCellValue(..., 'ReceivedBy', s.cpuserid, s.cpusername) does client-side.
  const currentUserName = useAuthStore(st => st.user?.name ?? '');

  // master
  const [pos, setPos] = useState<ReceivePoRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** The receipts list is searched SERVER-side (8k+ rows, page-capped), so the
   *  box is debounced — filtering client-side would only ever search the page
   *  that happened to be loaded, which reads as "no results" for anything older. */
  const [searchApplied, setSearchApplied] = useState('');
  /** History swaps the VIEW: legacy's Receiveview action replaces the
   *  purchase-order master with the flat list of RECEIPTS
   *  (Distribution_ListView_ReceiveView), which is where Rollback and the Date
   *  Filter live. */
  const [history, setHistory] = useState(false);
  const [dateFilter, setDateFilter] = useState<DateFilterId>(DEFAULT_DATE_FILTER);

  /** Row-selection ticks on the PURCHASE-ORDER master (owner request
   *  2026-09-25). Legacy sets ShowSelectionColumn="False" on
   *  Requisition_ListView_Receive_MainReceive, so this column is an AI addition
   *  rather than a port. Separate from histSel, which is the receipts view's own
   *  selection and drives Rollback. */
  const [poSel, setPoSel] = useState<Set<string | number>>(new Set());

  // history (the receipts list legacy's History action opens)
  const [receipts, setReceipts] = useState<ReceiveHistoryRow[]>([]);
  const [histSel, setHistSel] = useState<Set<string | number>>(new Set());
  const [rollingBack, setRollingBack] = useState(false);

  // detail
  const [openPo, setOpenPo] = useState<ReceivePoRow | null>(null);
  const [lines, setLines] = useState<PendingReceiveRow[]>([]);
  const [linesLoading, setLinesLoading] = useState(false);

  // Batch-edit state, keyed by row Oid — legacy's InlineEditMode="Batch" grid.
  const [edits, setEdits] = useState<Record<string, ReceiveEdit>>({});
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [lineSearch, setLineSearch] = useState('');
  const [receiving, setReceiving] = useState(false);

  const num = (v: number | null | undefined) => (v == null ? '' : String(v));

  const loadPos = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      // No history / months here: legacy's Date Filter targets the RECEIPTS
      // view, not this master, and History swaps the view rather than widening
      // this list — so the PO master is simply "what is still to receive".
      const res = await api.get('/api/v1/receive-items/purchase-orders', { params: { take: ICM_LIST_TAKE } });
      setPos((res.data as { items?: ReceivePoRow[] }).items ?? []);
    } catch {
      setError('Failed to load purchase orders.');
      setPos([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchApplied(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadReceipts = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/receive-items/history', {
        params: { take: ICM_LIST_TAKE, months: monthsOf(dateFilter), search: searchApplied || null },
      });
      const body = res.data as { items?: ReceiveHistoryRow[]; total?: number };
      const items = body.items ?? [];
      setReceipts(items);
      setHistSel(new Set());
      /* This list was capped at 500 of 8,394, so 6M / 1Y / 2Y / 5Y / ALL all
         showed the same 500 rows and the Date Filter looked dead above 3M
         (owner report 2026-10-01). */
      const note = icmTruncationNote(items.length, body.total);
      if (note) notify.info(note);
    } catch {
      setError('Failed to load the receipt history.');
      setReceipts([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateFilter, searchApplied, notify]);

  /** Whichever list the screen is currently showing — so Refresh, and every
   *  reload after an action, always re-reads the RIGHT one. */
  const reloadCurrent = useCallback(
    () => (history ? loadReceipts() : loadPos()),
    [history, loadReceipts, loadPos]);

  const loadLines = useCallback(async (poId: string) => {
    setLinesLoading(true);
    try {
      const res = await api.get('/api/v1/receive-items/pending', { params: { poId, take: ICM_LIST_TAKE } });
      const rows = (res.data as { items?: PendingReceiveRow[] }).items ?? [];
      setLines(rows);
      // Snapshot each row as loaded — the baseline a tick/untick restores to.
      const seed: Record<string, ReceiveEdit> = {};
      for (const r of rows) {
        const full = r.itemReceived ?? '';
        const ofAt = full.indexOf('of');
        const count = ofAt > 0 ? Number(full.slice(0, ofAt).trim()) || 0 : 0;
        seed[r.oid] = {
          receiving: 0,                       // [NonPersistent] — always 0 on load
          remaining: r.itemRemaining ?? 0,
          received: full,
          receiveDate: r.receiveDate,
          receivedBy: r.receivedByName,
          baseRemaining: r.itemRemaining ?? 0,
          baseReceivedCount: count,
          baseReceivedFull: full,
          source: '',
        };
      }
      setEdits(seed);
      setSelected(new Set());
    } catch {
      notify.error('Failed to load the lines for this purchase order.');
      setLines([]);
    } finally {
      setLinesLoading(false);
    }
  }, [notify]);

  useEffect(() => { void reloadCurrent(); }, [reloadCurrent]);

  /** The TOP-BAR title follows the view, as legacy's does — History opens
   *  Distribution_ListView_ReceiveView, captioned "Receiving Items History".
   *  Uses the shared `ai:topbar-title` override. */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title',
      { detail: history ? 'Receiving Items History' : null }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [history]);

  function openPurchaseOrder(po: ReceivePoRow) {
    setOpenPo(po);
    void loadLines(po.poId);
  }

  function backToList() {
    setOpenPo(null);
    setLines([]);
  }

  /** Legacy "POReport / Click here" — POReportPreview_Execute streams the
   *  report as yyyyMMddHHmmss.pdf, which the browser DOWNLOADS. It is fetched as
   *  an authenticated blob because a bare <a href> cannot carry the JWT, then
   *  saved via a download anchor.
   *
   *  It used to window.open() the blob, which rendered the PDF in a new tab
   *  instead of downloading it (user report 2026-09-25). No blocking "save as"
   *  dialog either: legacy downloads straight through on one click, so the
   *  shared saveBlobAs (which confirms first) would add a step legacy does not
   *  have. The filename comes from the server's Content-Disposition — the API
   *  already stamps the legacy yyyyMMddHHmmss.pdf — with a local fallback. */
  async function openPoReport(po: ReceivePoRow) {
    try {
      const res = await api.get<Blob>(`/api/v1/receive-items/${po.poId}/po-report`, { responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });

      const disposition = String(res.headers?.['content-disposition'] ?? '');
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
      const filename = match?.[1] ? decodeURIComponent(match[1].trim()) : `${stamp}.pdf`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on the next tick: revoking synchronously can cancel the download
      // in some browsers before it has read the blob.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      notify.success(`${filename} downloaded.`);
    } catch {
      notify.error('Failed to generate the PO report.');
    }
  }

  const filteredPos = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return pos;
    return pos.filter(p =>
      (p.poNumber ?? '').toLowerCase().includes(t) ||
      (p.vendorName ?? '').toLowerCase().includes(t) ||
      (p.trackingNumber ?? '').toLowerCase().includes(t)
    );
  }, [pos, search]);

  /** Ticked rows export on their own; with nothing ticked the whole list does —
   *  the ordinary grid convention, so the new tick column is not decoration. */
  const posExport      = poSel.size   ? filteredPos.filter(p => poSel.has(p.poId)) : filteredPos;
  const receiptsExport = histSel.size ? receipts.filter(r => histSel.has(r.oid))   : receipts;

  // Master columns — legacy order: POReport · Vendor · PO# · NumItem ·
  // ExpectDate · TrackingNumber.
  const poCols: AppColumn<ReceivePoRow>[] = [
    {
      field: 'poReport', header: 'POReport', width: 100,
      text: () => 'Click here',
      render: p => (
        <a role="button" tabIndex={0} className="bl-link"
          onClick={e => { e.stopPropagation(); void openPoReport(p); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); void openPoReport(p); } }}>
          Click here
        </a>
      ),
    },
    { field: 'vendorName',     header: 'Vendor',         width: 200, text: p => p.vendorName ?? '—', render: p => p.vendorName ?? '—' },
    { field: 'poNumber',           header: 'PO#',            width: 140, text: p => p.poNumber ?? '—', render: p => p.poNumber ?? '—' },
    { field: 'numItems',       header: 'NumItem',        width: 90, type: 'number', text: p => String(p.numItems), value: p => p.numItems },
    { field: 'expectDate',     header: 'ExpectDate',     width: 120, text: p => p.expectDate ? fmtLocalDate(p.expectDate) : '', render: p => p.expectDate ? fmtLocalDate(p.expectDate) : '' },
    { field: 'trackingNumber', header: 'TrackingNumber', width: 160, text: p => p.trackingNumber ?? '', render: p => p.trackingNumber ?? '' },
  ];

  /* Receiving Items HISTORY columns — the LIVE Distribution_ListView_ReceiveView
     set, in its order (BTLIMS.Web/Model.xafml, which overrides the module diff —
     the same "the running app wins" precedent as Workflow Status Chain):
       ReceiveID · Division · Item · Specification · Catalog# · PackUnits ·
       Item/Unit · Grade · Department · StockQty · ItemReceiving · ReceivedDate ·
       ReceivedBy · Status
     Read-only: legacy's own "Save" (ReceiveModify) batch-edits cells that belong
     to the ITEM master (Catalog#, PackUnits, Grade), so which of them are truly
     meant to be editable needs an owner call rather than a guess. Rollback —
     the action this view exists for — is ported. */
  const histCols: AppColumn<ReceiveHistoryRow>[] = [
    { field: 'receiveId',      header: 'ReceiveID',     width: 120, text: r => r.receiveId ?? '' },
    /* NO Division column, and the Item.Specification column is captioned
       "Description" — that is what the running GoLive grid shows (owner report
       2026-09-26), and the values line up exactly ("Mineral Stabilizer, 50mL"
       under both). The shipped Model.xafml disagrees: it puts Division at Index 1
       and leaves the caption as "Specification". The LIVE grid wins, the usual
       precedent here — its layout lives in dbo.ModelDifference, which this
       database does not carry, so the screenshot is the only authority. Only the
       CAPTION changed; the field behind it is unchanged. */
    { field: 'itemName',       header: 'Item',          width: 170, text: r => r.itemName ?? '' },
    { field: 'specification',  header: 'Description',   width: 230, text: r => r.specification ?? '' },
    { field: 'catalog',        header: 'Catalog#',      width: 110, text: r => r.catalog ?? '' },
    { field: 'packUnits',      header: 'PackUnits',     width: 90,  text: r => r.packUnits ?? '' },
    { field: 'itemUnit',       header: 'Item/Unit',     width: 85,  type: 'number', text: r => num(r.itemUnit), value: r => r.itemUnit ?? 0 },
    { field: 'gradeName',      header: 'Grade',         width: 100, text: r => r.gradeName ?? '' },
    { field: 'departmentName', header: 'Department',    width: 110, text: r => r.departmentName ?? '' },
    { field: 'stockQty',       header: 'StockQty',      width: 90,  type: 'number', text: r => num(r.stockQty), value: r => r.stockQty ?? 0 },
    { field: 'itemReceiving',  header: 'ItemReceiving', width: 110, type: 'number', text: r => num(r.itemReceiving), value: r => r.itemReceiving ?? 0 },
    { field: 'receiveDate',    header: 'ReceivedDate',  width: 140, text: r => (r.receiveDate ? fmtLocalDateTime(r.receiveDate) ?? '' : '') },
    { field: 'receivedByName', header: 'ReceivedBy',    width: 140, text: r => r.receivedByName ?? '' },
    { field: 'statusLabel',    header: 'Status',        width: 140, type: 'status', text: r => r.statusLabel },
  ];

  /** Rollback — legacy RollbackReceive_Execute (ReceiveViewController ~633): undo
   *  a receipt that should not have happened. Legacy asks for the reason in an
   *  ICMRollBack popup first, which is what the shared prompt does here, and
   *  refuses the whole set if ANY selected row has already been distributed —
   *  the server re-checks both, so this is the friendly half of the guard, not
   *  the guard itself. */
  async function handleRollback() {
    const ids = Array.from(histSel) as string[];
    if (ids.length === 0) { notify.warning('Select at least one receipt to roll back.'); return; }

    const blocked = receipts.filter(r => histSel.has(r.oid) && r.status !== 0);
    if (blocked.length > 0) {
      notify.error('This item has already been distributed, so the receipt cannot be rolled back.');
      return;
    }
    if (!(await confirm({
      title: 'Rollback?',
      message: `Roll back ${ids.length} receipt(s)? The quantity goes back onto the requisition and off the shelf.`,
      tone: 'danger',
      confirmLabel: 'Rollback',
    }))) return;

    const reason = await prompt({
      title: 'Rollback',
      message: 'Reason for the rollback',
      placeholder: 'Why is this receipt being reversed?',
      required: true,
    });
    if (reason == null) return;

    setRollingBack(true);
    try {
      for (const id of ids) {
        await api.post(`/api/v1/receive-items/${id}/rollback`, { reason });
      }
      setHistSel(new Set());
      await loadReceipts();
      notify.success('Rollback completed successfully.');
    } catch (err) {
      // The SP enforces the real rules ("already been distributed", "a reason is
      // required") and the API maps them to a 400 BUSINESS_RULE, so show what it
      // said rather than a generic failure.
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg ?? 'Rollback failed.');
    } finally {
      setRollingBack(false);
    }
  }

  /** Tick = "receive everything still outstanding on this line", exactly what
   *  legacy's SelectionChanged handler does (RequisitionViewcontroller.cs ~610):
   *  #ItemReceiving <- the row's remaining, #ItemRemain <- 0,
   *  #ItemRecieved <- "<total> of <total>", and stamp today / the current user.
   *  Unticking restores the row as loaded. A row whose quantity was TYPED is
   *  left alone by a tick (legacy's `sesselecttype != 'column'` guard), so the
   *  checkbox never silently overwrites a number the operator entered. */
  const applySelection = useCallback((next: Set<string | number>) => {
    setEdits(prev => {
      const out = { ...prev };
      for (const row of lines) {
        const e = out[row.oid];
        if (!e) continue;
        const ticked = next.has(row.oid);
        if (ticked && !selected.has(row.oid)) {
          if (e.source === 'column') continue;       // typed — leave it
          out[row.oid] = {
            ...e,
            receiving: e.baseRemaining,
            remaining: 0,
            received: `${row.totalItems} of ${row.totalItems}`,
            receiveDate: new Date().toISOString(),
            receivedBy: currentUserName,
            source: 'row',
          };
        } else if (!ticked && selected.has(row.oid)) {
          out[row.oid] = {
            ...e,
            receiving: 0,
            remaining: e.baseRemaining,
            received: e.baseReceivedFull,
            receiveDate: row.receiveDate,
            receivedBy: row.receivedByName,
            source: '',
          };
        }
      }
      return out;
    });
    setSelected(next);
  }, [lines, selected, currentUserName]);

  /** Typing a quantity into #ItemReceiving — legacy BatchEditEndEditing
   *  (RequisitionViewcontroller.cs ~683). A quantity above what is outstanding
   *  is CLAMPED to the remainder rather than rejected, and 0 or less resets the
   *  row and unticks it. Either way the row's other three cells follow. */
  const commitReceiving = useCallback((row: PendingReceiveRow, raw: string) => {
    const typed = Number(raw);
    setEdits(prev => {
      const e = prev[row.oid];
      if (!e) return prev;
      const total = row.totalItems;

      if (!Number.isFinite(typed) || typed <= 0) {
        setSelected(sel => { const n = new Set(sel); n.delete(row.oid); return n; });
        return { ...prev, [row.oid]: {
          ...e, receiving: 0, remaining: e.baseRemaining, received: e.baseReceivedFull,
          receiveDate: row.receiveDate, receivedBy: row.receivedByName, source: '' as const,
        } };
      }

      const fits = typed <= total && typed <= e.baseRemaining;
      const qty  = fits ? typed : e.baseRemaining;      // clamp, as legacy does
      const cal  = qty + e.baseReceivedCount;
      const rem  = Math.max(0, e.baseRemaining - qty);
      setSelected(sel => { const n = new Set(sel); n.add(row.oid); return n; });
      return { ...prev, [row.oid]: {
        ...e,
        receiving: qty,
        remaining: rem,
        received: `${Math.min(cal, total)} of ${total}`,
        receiveDate: new Date().toISOString(),
        receivedBy: e.receivedBy ?? currentUserName,
        source: 'column' as const,
      } };
    });
  }, [currentUserName]);

  /** Save = commit every line carrying a quantity, in ONE batch — legacy's Save
   *  action on this view commits the whole batch edit, not one row at a time. */
  async function handleSave() {
    const payload = lines
      .map(r => ({ row: r, e: edits[r.oid] }))
      .filter(x => (x.e?.receiving ?? 0) > 0)
      .map(x => ({ requisitionId: x.row.oid, receiveQty: x.e!.receiving, expiryDate: null, vendorLt: null }));

    if (payload.length === 0) {
      notify.warning('Tick a line, or enter a quantity in #ItemReceiving, before saving.');
      return;
    }
    /* The RC# the receipt mints. AI_ICM_Distribution_Receive_SP returns it as an
       @ReceiveId OUTPUT, the writer passes it back and the endpoint returns it —
       the page was throwing that response away, so the id never reached the
       user (owner request 2026-09-29: "ID should be displayed while save").
       Read back in successDialog, which useSaveFlow evaluates AFTER the save. */
    let mintedReceiveId: string | null = null;

    setReceiving(true);
    try {
      await saveFlow({
        formName: 'Receiving Items',
        auditAction: 'edit',
        // The API takes the WHOLE batch — `lines` — which the page had not been
        // sending: it posted { purchaseOrderLineId, quantity, vendorLt, storage,
        // comment }, none of which bind to ReceiveItemsInput, so `Lines` arrived
        // null and nothing was ever received.
        save: async () => {
          const res = await api.post<string>('/api/v1/receive-items', { lines: payload });
          mintedReceiveId = typeof res.data === 'string' ? res.data : null;
        },
        /* §13 milestone-success MODAL carrying the minted RC#. §18.6 bars a
           plain toast from echoing a new record id but allows exactly this
           ("a milestone-success dialog may show the minted ID").
           NOTE this is MORE than legacy: ReceiveViewController ~528 shows only
           the generic "Savesuccess" and never the ReceiveID. Added on owner
           request, not for GoLive parity. */
        successDialog: () => ({
          type: 'success' as const,
          title: 'Items Received',
          idLabel: 'Receive #',
          idValue: mintedReceiveId ?? '',
          rows: [
            { label: 'PO #', value: openPo?.poNumber ?? '—' },
            { label: 'Lines received', value: String(payload.length) },
            { label: 'Total quantity', value: String(payload.reduce((s, l) => s + (l.receiveQty ?? 0), 0)) },
          ],
        }),
        onSaved: async () => {
          if (openPo) await loadLines(openPo.poId);
          await loadPos();
        },
      });
    } finally {
      setReceiving(false);
    }
  }

  /* The legacy Requisition_ListView_Receive column set, in the order the LIVE
     grid shows it (the running app's ModelDifference wins over the shipped
     Model.xafml, the same precedent as Workflow Status Chain — the xafml hides
     Department and orders Catalog# before Description, the live grid does not):
       Item . Description . Catalog# . Department . TotalItems . #ItemReceiving .
       #ItemRecieved . #ItemRemain . OrderQty . PackUnits . Item/Unit .
       ReceiveDate . ReceivedBy . StockQty . Status
     Only #ItemReceiving is editable — every other column is AllowEdit="False"
     in legacy, and the three cells beside it are DRIVEN by it. */
  const lineCols: AppColumn<PendingReceiveRow>[] = [
    { field: 'itemName',       header: 'Item',           width: 170, text: r => r.itemName },
    { field: 'specification',  header: 'Description',    width: 230, text: r => r.specification ?? '', render: r => r.specification ?? '' },
    { field: 'catalog',        header: 'Catalog#',       width: 110, text: r => r.catalog ?? '', render: r => r.catalog ?? '' },
    { field: 'departmentName', header: 'Department',     width: 110, text: r => r.departmentName ?? '', render: r => r.departmentName ?? '' },
    { field: 'totalItems',     header: 'TotalItems',     width: 95,  type: 'number', text: r => String(r.totalItems), value: r => r.totalItems },
    {
      field: 'itemReceiving',  header: '#ItemReceiving', width: 120, type: 'number',
      // The one editable cell — legacy's [NonPersistent] scratch quantity.
      edit: perm.write ? { type: 'number' } : undefined,
      text:  r => String(edits[r.oid]?.receiving ?? 0),
      value: r => edits[r.oid]?.receiving ?? 0,
    },
    { field: 'itemReceived',   header: '#ItemRecieved',  width: 115, text: r => edits[r.oid]?.received ?? '', render: r => edits[r.oid]?.received ?? '' },
    { field: 'itemRemaining',  header: '#ItemRemain',    width: 105, type: 'number', text: r => String(edits[r.oid]?.remaining ?? 0), value: r => edits[r.oid]?.remaining ?? 0 },
    { field: 'orderQty',       header: 'OrderQty',       width: 90,  type: 'number', text: r => String(r.orderQty), value: r => r.orderQty },
    { field: 'packUnits',      header: 'PackUnits',      width: 95,  text: r => r.packUnits ?? '', render: r => r.packUnits ?? '' },
    { field: 'itemUnit',       header: 'Item/Unit',      width: 90,  type: 'number', text: r => num(r.itemUnit), value: r => r.itemUnit ?? 0 },
    {
      field: 'receiveDate',    header: 'ReceiveDate',    width: 115,
      text:   r => { const d = edits[r.oid]?.receiveDate; return d ? fmtLocalDate(d) ?? '' : ''; },
      render: r => { const d = edits[r.oid]?.receiveDate; return d ? fmtLocalDate(d) ?? '' : ''; },
    },
    { field: 'receivedByName', header: 'ReceivedBy',     width: 115, text: r => edits[r.oid]?.receivedBy ?? '', render: r => edits[r.oid]?.receivedBy ?? '' },
    { field: 'stockQty',       header: 'StockQty',       width: 95,  type: 'number', text: r => num(r.stockQty), value: r => r.stockQty ?? 0 },
    { field: 'statusLabel',    header: 'Status',         width: 130, type: 'status', text: r => r.statusLabel ?? '' },
  ];

  const filteredLines = useMemo(() => {
    const q = lineSearch.trim().toLowerCase();
    if (!q) return lines;
    return lines.filter(r =>
      (r.itemName ?? '').toLowerCase().includes(q) ||
      (r.specification ?? '').toLowerCase().includes(q) ||
      (r.catalog ?? '').toLowerCase().includes(q) ||
      (r.departmentName ?? '').toLowerCase().includes(q));
  }, [lines, lineSearch]);

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            {openPo ? (
              <>
                <span onClick={backToList} style={{ cursor: 'pointer' }}>Receiving Items</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">{openPo.poNumber}</span>
              </>
            ) : history ? (
              <>
                {/* The way BACK, now that History drops the "Pending" button. */}
                <span onClick={() => { setHistory(false); setSearch(''); setHistSel(new Set()); }}
                      style={{ cursor: 'pointer' }}>Receiving Items</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">Receiving Items History</span>
              </>
            ) : (
              <span className="dp-bc-active">Receiving Items</span>
            )}
          </div>
          <div className="dp-title-row">
            <FormTitle
              pageId="icm-receive-items" auditFormName="Receiving Items"
              title={history && !openPo ? 'Receiving Items History' : 'Receiving Items'}
              loadMs={loadMs}
              loading={loading}
            />
          </div>
        </div>
      </div>

      {openPo ? (
        /* ── DETAIL: the pending lines of one PO ── */
        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            {/* Legacy's bar on this view is Save . Export to . Search — there is
                no per-row "Receive" button; you tick lines (or type a quantity)
                and Save commits the batch.
                NO Back button (owner request 2026-09-29): legacy has none, and
                returning to the list is the breadcrumb's "Receiving Items"
                crumb, which already calls backToList — so nothing is lost by
                dropping it, and the bar matches legacy. Refresh stays; it is the
                canonical reload action on every form. */}
            <AppToolbar
              left={perm.write ? (
                <button className="ca-btn" onClick={handleSave} disabled={receiving}>
                  <BusyIcon busy={receiving} icon="ti-device-floppy" /> Save
                </button>
              ) : undefined}
              right={
                <div className="list-search" style={{ minWidth: 240 }}>
                  <span className="tb-i">🔍</span>
                  <input type="text" value={lineSearch} onChange={e => setLineSearch(e.target.value)} placeholder="Text to search…" />
                  {lineSearch && <span className="list-search-clear" onClick={() => setLineSearch('')}>×</span>}
                </div>
              }
              onRefresh={() => loadLines(openPo.poId)}
              refreshing={linesLoading}
              exportData={{ filename: `receive-${openPo.poNumber ?? 'po'}`, ...gridExportMatrix(filteredLines, lineCols) }}
            />
          </div>
          {/* NO PO#/Vendor/ExpectDate/TrackingNumber header card: legacy's
              Requisition_ListView_Receive is the GRID ALONE (owner report
              2026-09-25 "only the grid is available, others not available").
              AI had added a read-only summary panel above it that legacy does
              not have. The PO number stays visible in the breadcrumb, and all
              four values are columns on the MASTER list you arrive from, so
              nothing is lost by dropping the panel. */}
          {/* SIZE TO CONTENT, not to the page (owner report 2026-09-25 "adjust the
              grid height"). `bl-card-flex` is `flex:1` and this card is a direct
              child of the flex-column `.bl-grid-area`, so the grid stretched to
              the full page: its rows sat at the top and its footer was pushed to
              the very bottom, with a tall empty band between them. Legacy's
              detail grid ends immediately under its rows. The MASTER grid below
              keeps `bl-card-flex` — a 20-row page genuinely fills it. */}
          <div className="bl-card icm-detail-card">
            <AppDataGrid<PendingReceiveRow>
              rows={filteredLines}
              columns={lineCols}
              getRowId={r => r.oid}
              loading={linesLoading}
              loadMs={null}
              selectable
              selectedIds={selected}
              onSelectionChange={applySelection}
              onCellCommit={(row, col, value) => {
                if (col.field === 'itemReceiving') commitReceiving(row, value);
              }}
              emptyText="No lines pending receipt on this purchase order."
            />
          </div>
        </div>
      ) : (
        /* ── MASTER: the PO list, or — in History — the RECEIPTS ── */
        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            {/* History flips the whole VIEW, exactly as legacy's Receiveview
                action does: out goes the purchase-order master, in comes the
                flat list of receipts. Rollback and the Date Filter belong to
                that view only — legacy targets both at
                Distribution_ListView_ReceiveView — so they appear with it. */}
            <AppToolbar
              left={
                <>
                  {/* The History toggle belongs to the PO master only — legacy
                      swaps the whole view, and its receipts view has no way back
                      on the bar (the breadcrumb is it). */}
                  {!history && (
                    <button className="ca-btn" onClick={() => { setHistory(true); setSearch(''); setPoSel(new Set()); }}
                      title="Show every receipt">
                      <i className="ti ti-history" /> History
                    </button>
                  )}
                  {history && (
                    <>
                      {perm.write && <button className="ca-btn" onClick={handleRollback} disabled={rollingBack}>
                        <BusyIcon busy={rollingBack} icon="ti-arrow-back-up" /> Rollback
                      </button>}
                      <DateFilterSelect value={dateFilter} onChange={setDateFilter} />
                    </>
                  )}
                </>
              }
              onRefresh={reloadCurrent}
              refreshing={loading}
              exportData={history
                ? { filename: 'receive-history', ...gridExportMatrix(receiptsExport, histCols) }
                : { filename: 'receive-items', ...gridExportMatrix(posExport, poCols) }}
            />
          </div>
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={history ? 'Search ReceiveID, item, catalog#…' : 'Search PO#, vendor, tracking…'} />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
            ) : history ? (
              <AppDataGrid<ReceiveHistoryRow>
                rows={receipts}
                columns={histCols}
                getRowId={r => r.oid}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={histSel}
                onSelectionChange={setHistSel}
                emptyText="No receipts for this period."
              />
            ) : (
              <AppDataGrid<ReceivePoRow>
                rows={filteredPos}
                columns={poCols}
                getRowId={p => p.poId}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={poSel}
                onSelectionChange={setPoSel}
                onRowClick={openPurchaseOrder}
                emptyText="No purchase orders pending receipt."
              />
            )}
          </div>
        </div>
      )}

    </div>
  );
}
